import { useTranslation } from 'react-i18next';
import {
	Typography,
	Card,
	CardContent,
	Box,
	IconButton,
	Tabs,
	Tab,
} from '@mui/material';
import { DefaultValues, FormProvider, useForm } from 'react-hook-form';
import { useCallback, useEffect, useState } from 'react';
import AppDataGrid, {
	initialPagination,
} from '@/common/components/dataGrid/AppDataGrid';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { GridColDef } from '@mui/x-data-grid';
import qs from 'qs';
import { filterObjectByKeys, parseSummarySearchParams } from '@/utils/qsHelper';
import { DISPLAY_DATE_FORMAT } from '@/utils/dateHelper';
import { getIntakeDueIcon } from '@/utils/caseAlertHelper';
import dayjs from 'dayjs';
import VisibilityOutlinedIcon from '@mui/icons-material/VisibilityOutlined';
import CentrePendingIntakeSummaryFilterSection from './components/CentrePendingIntakeSummaryFilterSection';
import {
	admissionRequestFilter,
	AdmissionRequestPageDTO,
} from '@/api/DTO/member-management/admissionRequest';
import { useGetPendingListQuery } from '@/api/member-management/admissionRequest';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import AccessAlarmIcon from '@mui/icons-material/AccessAlarm';
import MemberStatusChip from '@/common/components/MemberStatusChip';

export default function CentrePendingIntakeSummaryPage() {
	const { t } = useTranslation();
	const [searchParams, setSearchParams] = useSearchParams();
	const [isInitializedForm, setIsInitializedForm] = useState<boolean>(false);
	const navigate = useNavigate();
	const [currentTab, setCurrentTab] = useState('centre');

	const handleTabChange = (event: React.SyntheticEvent, newValue: string) => {
		setCurrentTab(newValue);
		if (newValue == 'my') navigate(`/member-management/pending-intake-list`);
		if (newValue == 'centre')
			navigate(`/member-management/pending-intake-list/centre`);
	};

	const defaultPropertyFilter: admissionRequestFilter = {
		pagination: initialPagination,
		searchString: '',
		sortings: [],
		isCaseStatusList: [],
		statusList: [],
		actionRequiredByList: [],
	};
	const defaultValues: DefaultValues<admissionRequestFilter> =
		defaultPropertyFilter;
	const methods = useForm<admissionRequestFilter>({
		defaultValues,
	});
	const transformedSearchParams: admissionRequestFilter = {
		...defaultPropertyFilter,
		...filterObjectByKeys(parseSummarySearchParams(searchParams), [
			'pagination',
			'sortings',
			'searchString',
			'isCaseStatusList',
			'statusList',
			'actionRequiredByList',
		]),
	};
	const { data: propertySummary, isSuccess: isGetPropertySummarySuccess } =
		useGetPendingListQuery(
			{ ...transformedSearchParams, my: 0 },
			{
				refetchOnMountOrArgChange: true,
			}
		);

	const transformResponse = useCallback(
		(response?: AdmissionRequestPageDTO[]) => {
			if (response == null) return;
			const newResponse: any[] = [];
			response.forEach((item) => {
				newResponse.push({
					...item,
					receivingReferralOrApplicationDate:
						!item.receivingReferralOrApplicationDate
							? '-'
							: dayjs(item.receivingReferralOrApplicationDate)
									.locale('en')
									.format(DISPLAY_DATE_FORMAT),
					dueDateOfIntakeCompletion: !item.dueDateOfIntakeCompletion
						? '-'
						: dayjs(item.dueDateOfIntakeCompletion)
								.locale('en')
								.format(DISPLAY_DATE_FORMAT),
					intakeDueIcon: getIntakeDueIcon(item),
				});
			});
			return newResponse;
		},
		[]
	);

	// watch serach params
	useEffect(() => {
		if (!isInitializedForm) {
			methods.reset(transformedSearchParams, {
				keepDefaultValues: true,
			});
			setIsInitializedForm(true);
		}
	}, [searchParams]);

	// watch fields change
	useEffect(() => {
		const subscription = methods.watch((value) => {
			const propertyFilterFormResult = value as admissionRequestFilter;

			const cleanedFilter = Object.fromEntries(
				Object.entries(propertyFilterFormResult).filter(([key, val]) => {
					return val !== '' && val != null;
				})
			);

			// append to url to reload page
			setSearchParams(qs.stringify(cleanedFilter));
		});
		return () => subscription.unsubscribe();
	}, [methods.watch]);

	const columns: GridColDef[] = [
		{
			field: 'preAdmissionNo',
			headerName: t('member.preAdmissionNo'),
			flex: 15,
		},
		{
			field: 'fileNo',
			headerName: t('member.fileNo'),
			flex: 15,
		},
		{
			field: 'nameEn',
			headerName: t('member.name'),
			flex: 15,
			renderCell: (params) => (
				<Box>
					<Typography variant='body2'>{params.row.nameEn}</Typography>
					<Typography variant='body2'>{params.row.nameZh}</Typography>
				</Box>
			),
		},
		{
			field: 'receivingReferralOrApplicationDate',
			headerName: t('member.dateReceivingReferralApplication'),
			flex: 25,
		},
		{
			field: 'dueDateOfIntakeCompletion',
			headerName: t('member.dueDateIntakeCompletion'),
			flex: 25,
			renderCell: (params) => (
				<>
					{params.row.dueDateOfIntakeCompletion}
					{params.row.intakeDueIcon == 'completed' ? (
						<CheckCircleOutlineIcon />
					) : params.row.intakeDueIcon == 'overdue' ? (
						<ErrorOutlineIcon />
					) : params.row.intakeDueIcon == 'dueSoon' ? (
						<AccessAlarmIcon />
					) : (
						''
					)}
				</>
			),
		},

		{
			field: 'phone',
			headerName: t('member.mobilePhone'),
			flex: 15,
		},
		{
			field: 'isCase',
			headerName: t('member.isCase'),
			flex: 15,
			renderCell: (params) => (params.row.isCase ? 'Yes' : 'No'),
		},
		{
			field: 'statusDesc',
			headerName: t('member.status'),
			flex: 15,
			maxWidth: 140,
			minWidth: 140,
			renderCell: (params) => (
				<MemberStatusChip
					status={params.row.status}
					label={params.row.statusDesc}
				/>
			),
		},
		{
			field: 'actionRequiredBy',
			headerName: t('member.actionRequiredby'),
			flex: 15,
		},
		{
			field: 'edit',
			headerName: '',
			width: 68,
			renderCell: (params) => (
				<Link
					to={`/member-management/admission-request-list/detail/${params.id}`}
					state={{
						propertySummarySearchParams: qs.stringify(transformedSearchParams),
					}}
				>
					<IconButton color='secondary'>
						<VisibilityOutlinedIcon />
					</IconButton>
				</Link>
			),
		},
	];

	return (
		<>
			<Box display='flex' justifyContent='space-between'>
				<Typography variant='h4'>
					{t('drawerListItems.pendingSummary')}
				</Typography>
			</Box>

			<Card sx={{ marginTop: 3 }}>
				<CardContent>
					{' '}
					<Tabs
						value={currentTab}
						onChange={handleTabChange}
						sx={{ marginBottom: 3 }}
					>
						<Tab
							label={
								<Typography variant='body1'>{t('member.myPending')}</Typography>
							}
							value='my'
							sx={{ textTransform: 'none' }}
						/>
						<Tab
							label={
								<Typography variant='body1'>
									{t('member.centrePending')}
								</Typography>
							}
							value='centre'
							sx={{ textTransform: 'none' }}
						/>
					</Tabs>
					<FormProvider {...methods}>
						<CentrePendingIntakeSummaryFilterSection></CentrePendingIntakeSummaryFilterSection>

						<Box marginTop={2}>
							<AppDataGrid
								loading={!isGetPropertySummarySuccess}
								rows={transformResponse(propertySummary?.data?.items) ?? []}
								rowCount={propertySummary?.data?.pagination.totalItems ?? 0}
								columns={columns}
								getRowId={(row) => row.id}
							/>
						</Box>
					</FormProvider>
				</CardContent>
			</Card>
		</>
	);
}
